import * as Icons from "@mui/icons-material";
import { LocalShipping } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useContext, useState } from "react";
import { throttle } from "throttle-typescript";

import AvatarWithStatus from "@/components/AvatarWithStatus";
import FeedItemModals from "@/components/feed/shared/FeedItemModals";
import FeedItemStatus from "@/components/feed/shared/FeedItemStatus";
import { useFeedItemData } from "@/components/feed/shared/useFeedItemData";
import type { Feed } from "@/db/types";
import Locator from "@/locator";
import type { FullItem } from "@/models/FeedContextProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { defaultFocusStyles, menuItemFocusSX } from "@/utils";
import Loading from "../../Loading/Index";
import PlaybackIndicator from "../../PlaybackIndicator/Index";
import ClassicFeedItemContent from "./ClassicFeedItemContent";

type Props = {
  item: FullItem;
  feed: Feed;
  vListIndex?: number;
  active?: boolean;
  highlightPodcast: boolean;
  setItemNotLoaded?: (value: string) => void;
  preventScroll?: (value: boolean) => void;
  aliasChannelOwnerId?: string;
};

export default function ClassicFeedItem({
  item,
  feed,
  active,
  highlightPodcast,
  setItemNotLoaded,
  preventScroll,
  aliasChannelOwnerId,
}: Props) {
  const data = useFeedItemData({ item, feed, active, highlightPodcast, preventScroll, aliasChannelOwnerId });
  const theme = useTheme();
  const { ampli } = useContext(TrackingContext);

  const [copiedTranscript, setCopiedTranscript] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const [anchorElOpen, setAnchorElOpen] = useState(false);
  const [triggerStatusModalOpen, setTriggerStatusModalOpen] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);

  const open = Boolean(anchorEl);

  if (!item || !data.shouldRender) return null;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    ampli.viewMessageContextMenu();
  };

  const _handleKeyUp = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const keyCode = event.key.toLowerCase();
    if (keyCode === " " || keyCode === "enter") {
      setAnchorElOpen(!anchorElOpen);
    }
  };
  const handleKeyUp = throttle(_handleKeyUp, 100);

  const handleMessageMenuClose = () => {
    setAnchorEl(null);
    setAnchorElOpen(false);
  };

  const handleCopy = (value: string, setter: (fn: () => boolean) => void) => {
    setter(() => true);
    navigator.clipboard.writeText(value);
    setTimeout(() => setter(() => false), 3000);
  };

  const handleStatusOpen = () => setTriggerStatusModalOpen(() => true);

  const fetchGeoFromLocationIds = async () => {
    try {
      const { client } = await import("@/config");
      const fetchLocations = await client.getLocationsFromItemIds({
        workspaceId: feed.workspaceId,
        itemIds: [item.id],
      });
      const matchLocations = fetchLocations?.locations?.find((location) => location.itemId === item.id);
      const found = matchLocations?.location ? matchLocations.location : null;
      if (found?.latitude && found?.longitude) return found;
      return Promise.reject("No location found");
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const sendFleetMessage = async () => {
    setDisabled(() => true);
    try {
      const location = await fetchGeoFromLocationIds();
      data.setFleetMessageLatitude(location.latitude.toString());
      data.setFleetMessageLongitude(location.longitude.toString());
      data.setFleetMessageText(data.displayArtifact);
      data.setFleetMessageFromItem(true);
      ampli.createFleetMessageFromDriverMessage();
    } catch (e) {
      console.error(e);
      data.setFleetMessageInvalidLocation(true);
    }
    data.setFleetMessageModalOpen(true);
    data.setFleetMessageActiveSection("geo-target");
    setDisabled(() => false);
  };

  const paperColor = () => {
    if (data.hasCallRecord) return theme.palette.secondary.dark;
    if (data.displayArtifactRecord) return theme.palette.brand.other.teal.main;
    return data.mine ? theme.palette.brand.primary.dark : theme.palette.neutral.dark;
  };

  const messageGridStyles = () => ({
    gridTemplateAreas: {
      xs: `"avatar info info info info"
"avatar message message message message"
"avatar details details details details"`,
      sm:
        data.mine || data.isCurrentFeedAdmin
          ? `"avatar info info info ."
"avatar message message message message"
"avatar details details details ."`
          : `"avatar info info info ."
"avatar message message message ."
"avatar details details details ."`,
    },
    gridTemplateColumns: "40px 1fr 1fr 1fr 34px",
  });

  const itemStyles = {
    boxSizing: "border-box",
    background: paperColor,
    borderRadius: "16px",
    border: () => {
      let color = data.mine ? theme.palette.brand.primary.light : theme.palette.secondary.light;
      if (data.displayArtifactRecord) color = theme.palette.brand.other.teal.dark;
      if (data.hasCallRecord) color = theme.palette.secondary.main;
      return `1px solid ${color}`;
    },
  };

  return (
    <>
      <FeedItemModals {...data.feedItemModalsProps} />
      <Stack
        id={item.id}
        ref={data.ref}
        direction={"row"}
        sx={{
          alignItems: "center",
          justifyContent: data.mine ? "flex-end" : "flex-start",
          width: "100%",
          height: "auto",
          minHeight: 180,
          borderRadius: "16px",
          py: 0.5,
          transition: "background 0.3s linear",
          background: active ? theme.palette.brand.primary.darkHighlight : "transparent",
        }}
        useFlexGap={true}
        spacing={2}
        aria-label={Locator.feed.items.item}
      >
        <Box
          sx={{
            display: "grid",
            ...messageGridStyles(),
            alignItems: "center",
            gap: 1,
            width: "100%",
            maxWidth: "541px",
            pl: data.mine ? 1 : 2,
            pr: data.mine ? 2 : 1,
            alignSelf: "flex-end",
          }}
          role="presentation"
        >
          <Box sx={{ alignSelf: "flex-start", gridArea: "avatar", py: 3 }}>
            {!data.mine && data.activeItemId !== item.id && (
              <>
                {data.displayArtifactRecord ? (
                  <Avatar
                    sx={{
                      fontSize: "20px",
                      width: 40,
                      height: 40,
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      background: theme.palette.brand.other.teal.light,
                      border: `1.5px solid ${theme.palette.brand.other.teal.dark}`,
                    }}
                  >
                    <Icons.CampaignOutlined />
                  </Avatar>
                ) : (
                  <AvatarWithStatus accountId={item.account?.id} key={item.account?.id} />
                )}
              </>
            )}
            {data.activeItemId === item.id && <PlaybackIndicator playing={data.playerPlaying} />}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: data.mine ? "flex-end" : "flex-start",
              gridArea: "info",
              gap: 1,
            }}
            color={theme.palette.secondary.light}
            role="presentation"
          >
            <Typography
              id={`${data.feedItemId}-user`}
              aria-label={data.messageWrapperLabel}
              sx={{
                fontSize: "12px",
                fontWeight: "medium",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              {item?.isSilent ? (
                <Tooltip title="Silent Message">
                  <Icons.VolumeOffOutlined
                    fontSize="small"
                    sx={{
                      mr: 0.25,
                      color: theme.palette.primary.main,
                      background: theme.palette.error.main,
                      borderRadius: 1,
                    }}
                  />
                </Tooltip>
              ) : null}
              {data.accountName ? `${data.accountName} • ` : null}
              {data.itemDate}
            </Typography>
            {data.unread ? (
              <Box
                sx={{
                  height: 8,
                  width: 8,
                  background: theme.palette.brand.secondary.main,
                  borderRadius: "100%",
                }}
              />
            ) : null}
          </Box>
          <Stack
            sx={{
              width: "100%",
              alignItems: "center",
              justifyContent: data.mine ? "flex-end" : "flex-start",
              gridArea: "message",
            }}
            direction={"row"}
            role="presentation"
          >
            <Paper
              variant="outlined"
              id={`${data.feedItemId}-wrapper`}
              sx={{
                ...itemStyles,
                width: "100%",
                maxWidth: data.isProcessing ? "120px" : "100%",
                position: "relative",
              }}
            >
              <Box sx={{ width: "100%", p: "14px" }}>
                {data.isProcessing ? (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Tooltip title="Click to load content">
                      <Button onClick={() => setItemNotLoaded(item.id)}>
                        <Loading variant="white" size="large" baseSize={6} />
                      </Button>
                    </Tooltip>
                  </Box>
                ) : (
                  <ClassicFeedItemContent
                    itemFeedId={item.feedId}
                    mine={data.mine}
                    feedItemId={data.feedItemId}
                    shouldShowAudioTranscriptContent={data.shouldShowAudioTranscriptContent}
                    hasCallRecord={data.hasCallRecord}
                    filesOrLinks={data.filesOrLinks}
                    duration={data.duration}
                    itemId={item.id}
                    hasPlayableAudio={data.hasPlayableAudio}
                    onPlay={data.handlePlay}
                    onFinished={data.handleFinished}
                    onPause={data.handlePause}
                    displayArtifactRecord={data.displayArtifactRecord}
                    showModalTranscription={data.showModalTranscription}
                    setShowModalTranscription={data.setShowModalTranscription}
                    feedId={item.feedId}
                    showTranscription={data.showTranscription}
                    preferredLanguage={data.preferredLanguage}
                    transcriptions={data.transcriptions}
                    transcriptionContent={data.transcriptionContent}
                    unreliableTranscription={data.unreliableTranscription}
                    unreliableTranscriptLabel={data.unreliableTranscriptLabel}
                    transcriptTranslatedFrom={data.transcriptTranslatedFrom}
                    hasTranscription={data.hasTranscription}
                    files={data.files}
                    links={data.links}
                    hasDisplayArtifact={(value: string) => data.setDisplayArtifact(() => value)}
                    callRecords={data.callRecords}
                  />
                )}
              </Box>
            </Paper>
            {data.MenuOptions.showOptions && (
              <Box sx={{ p: "8px" }}>
                <IconButton
                  color="secondary"
                  aria-label={Locator.feed.items.actions.main}
                  onClick={handleClick}
                  onKeyUp={handleKeyUp}
                  sx={{
                    ...(anchorElOpen ? defaultFocusStyles : {}),
                    height: "100%",
                    px: 0,
                    py: 1,
                    color: theme.palette.primary.main,
                  }}
                >
                  <Icons.MoreVert role="img" />
                </IconButton>
              </Box>
            )}

            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{ mt: 1, ...menuItemFocusSX }}
              open={open}
              onClose={handleMessageMenuClose}
              slotProps={{ paper: { "aria-label": Locator.feed.items.actions.menu } }}
            >
              {data.MenuOptions.showMessageDetails ? (
                <MenuItem onClick={handleStatusOpen}>
                  <ListItemIcon>
                    <Icons.InfoOutlined role="img" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>Message Details</ListItemText>
                </MenuItem>
              ) : null}
              {data.MenuOptions.canCopy ? (
                <MenuItem
                  onClick={() => handleCopy(item?.url, setCopiedLink)}
                  aria-label={Locator.feed.items.actions.copyLink}
                >
                  <ListItemIcon>
                    {copiedLink ? <Icons.Done role="presentation" /> : <Icons.Link role="presentation" />}
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>
                    {copiedLink ? "Copied!" : "Copy Link"}
                  </ListItemText>
                </MenuItem>
              ) : null}
              {data.MenuOptions.canCopyTranscript ? (
                <MenuItem onClick={() => handleCopy(data.textTranscript?.transcriptionContent, setCopiedTranscript)}>
                  <ListItemIcon>
                    {copiedTranscript ? <Icons.Done role="presentation" /> : <Icons.ContentCopy role="presentation" />}
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>
                    {copiedTranscript ? "Copied!" : "Copy transcript"}
                  </ListItemText>
                </MenuItem>
              ) : null}
              {data.MenuOptions.canDelete && !data.userReadOnlyMode && (
                <MenuItem
                  onClick={() => {
                    data.handleSetConfirmDeleteDialog(true);
                    handleMessageMenuClose();
                  }}
                  aria-label={Locator.feed.items.actions.delete}
                >
                  <ListItemIcon>
                    <Icons.Delete role="img" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>Delete Message</ListItemText>
                </MenuItem>
              )}
              {data.MenuOptions.canSendFleetMessage && (
                <MenuItem
                  disabled={disabled}
                  onClick={() => sendFleetMessage()}
                  aria-label={Locator.feed.items.actions.sendFleetMessage}
                >
                  <ListItemIcon>
                    <LocalShipping />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>Send Fleet Message</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </Stack>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gridArea: "details",
            }}
          >
            <Box sx={{ order: data.mine ? 0 : 1 }}>
              <FeedItemStatus
                itemId={item.id}
                sender={data.senderAccount}
                allDriverEvents={data.allDriverEvents}
                count={data.count}
                groups={data.groups}
                createdAt={item.createdAt}
                openModal={triggerStatusModalOpen}
                setOpenModalStatus={() => setTriggerStatusModalOpen(() => false)}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {data.flags.debugMode && (
                <>
                  <IconButton
                    color="secondary"
                    size="small"
                    aria-label="Click to re-run pipeline for this FeedItem"
                    disabled={data.isProcessing}
                    onClick={data.refreshPipeline}
                  >
                    <Icons.RefreshOutlined role="img" />
                  </IconButton>
                  <IconButton
                    color="secondary"
                    size="small"
                    aria-label="Click for debug info"
                    onClick={() => data.handleSetDebuggerOpen(!data.debuggerOpen)}
                  >
                    <Icons.InfoOutlined role="img" />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Stack>
    </>
  );
}
